import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import useReturnedPaymentFeature from '@/hooks/useReturnedPaymentFeature';
import SupplierApprovalGate from '../SupplierApprovalGate/SupplierApprovalGate';
import successIcon from '@/assets/icons/success.svg';
import styles from './BulkStatusChangeDialog.module.css';

type DialogState = 'selectStatus' | 'loading' | 'success' | 'error';

interface BulkStatusChangeDialogProps {
  open: boolean;
  onClose: () => void;
  selectedRequests: ManagementSupportRequestDtoIn[];
  commonStatuses: string[];
  onSuccess: () => void;
}

interface BulkStatusUpdateResult {
  successCount: number;
}

interface SupportRequestIdentifier {
  correlationId: string;
  counter: number | null;
  isInInitialStatusProcess: boolean;
}

interface BulkUpdateSupportRequestStatusDto {
  items: SupportRequestIdentifier[];
  newStatus: string;
  comment?: string;
  showInPersonalArea: boolean;
  sendEmail?: boolean;
}

const BulkStatusChangeDialog = ({
  open,
  onClose,
  selectedRequests,
  commonStatuses,
  onSuccess,
}: BulkStatusChangeDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [dialogState, setDialogState] = useState<DialogState>('selectStatus');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [showInPersonalArea, setShowInPersonalArea] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When true, the supplier approval gate (issues + withholdings) is being
  // shown over this dialog before we actually fire the bulk-status PUT.
  const [gateOpen, setGateOpen] = useState(false);

  /* HAT-1395, the office's call: the coordinator is asked for another account one request at a
     time, from the request's own status select - each returned support handled on its own. In
     bulk «תשלום חזר» only changes the status, and the window says how to ask instead. */
  const isReturnedPaymentOn = useReturnedPaymentFeature();
  const paidToSupportedAccount = selectedRequests.filter(
    (r) => r.paymentMethodType === PaymentMethodType.BankAccount && !r.isThirdPartyPayment
  ).length;
  const returnedInBulk = isReturnedPaymentOn && selectedStatus === 'PaymentReturned';
  const mailsCoordinators = sendEmail;

  const handleStatusSelect = (status: string) => {
    setSelectedStatus(status);
  };

  const performBulkUpdate = useCallback(async () => {
    if (!currentUser?.token || !selectedStatus) return;

    setDialogState('loading');
    setError(null);

    const headers = { Authorization: `Bearer ${currentUser.token}` };
    const body: BulkUpdateSupportRequestStatusDto = {
      items: selectedRequests.map((request) => ({
        correlationId: request.multiRequestCorrelationId,
        counter: request.isInInitialStatusProcess ? null : (request.multiRequestCounter ?? null),
        isInInitialStatusProcess: request.isInInitialStatusProcess,
      })),
      newStatus: selectedStatus,
      comment: comment.trim() || undefined,
      showInPersonalArea,
      sendEmail,
    };

    try {
      await putAuth<BulkStatusUpdateResult, object, BulkUpdateSupportRequestStatusDto>(
        'management/support-requests/bulk-status',
        headers,
        {},
        body
      );
      setDialogState('success');
    } catch (err: unknown) {
      console.error('Error in bulk status update:', err);
      setDialogState('error');

      const axiosError = err as { response?: { data?: string } };
      if (axiosError.response?.data) {
        setError(axiosError.response.data);
      } else {
        setError(t('bulkSupportRequestStatus.failed'));
      }
    }
  }, [currentUser?.token, selectedStatus, selectedRequests, comment, showInPersonalArea, sendEmail, t]);

  // Open the supplier approval gate first; it will fall through to
  // performBulkUpdate when there's nothing to confirm or when the user
  // explicitly accepts both gate dialogs.
  const handleSubmit = () => {
    if (!currentUser?.token || !selectedStatus) return;
    setGateOpen(true);
  };

  const handleGateContinue = useCallback(() => {
    setGateOpen(false);
    performBulkUpdate();
  }, [performBulkUpdate]);

  const handleGateCancel = useCallback(() => {
    setGateOpen(false);
  }, []);

  const handleClose = () => {
    setDialogState('selectStatus');
    setSelectedStatus('');
    setComment('');
    setShowInPersonalArea(true);
    setSendEmail(false);
    setError(null);
    setGateOpen(false);
    onClose();
  };

  const handleSuccessClose = () => {
    handleClose();
    onSuccess();
  };

  // Retry-from-error path: skip the gate (the user already acknowledged it
  // before the first attempt failed) and re-fire the bulk update directly.
  const handleRetry = () => {
    performBulkUpdate();
  };

  return (
    <>
      <SupplierApprovalGate
        open={gateOpen}
        selectedRequests={selectedRequests}
        newStatus={selectedStatus}
        onContinue={handleGateContinue}
        onCancel={handleGateCancel}
      />
      {/* The frame of the single-request status window and of «עדכון חשבון בנק», sized to its
          content: a 400px box of its own read as another screen, and the «תשלום חזר» explanation
          wrapped a word a line in it. */}
      <MainDialogLayout
        open={open}
        onOpenChange={(isOpen: boolean) => !isOpen && handleClose()}
        title={t('bulkSupportRequestStatus.title')}
        maxWidth="720px"
        fitContent
      >

          {/* State 1: Select Status */}
          {dialogState === 'selectStatus' && (
            <div className={styles.content}>
              <p className={`body-md-regular ${styles.countMessage}`}>
                {t('bulkSupportRequestStatus.selectedRequestsCount', { count: selectedRequests.length })}
              </p>

              <div className={styles.selectSection}>
                <label className={`body-md-semibold ${styles.label}`}>
                  {t('bulkSupportRequestStatus.selectNewStatus')}
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => handleStatusSelect(e.target.value)}
                  className={styles.dropdown}
                >
                  <option value="">{t('bulkSupportRequestStatus.selectStatus')}</option>
                  {commonStatuses.map((status) => (
                    <option key={status} value={status}>
                      {t(status)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Why there is no «בקשת פרטי חשבון חדשים» here: it is asked a request at a time, from the
                  request's own row. Said, so the missing toggle does not read as the feature gone. */}
              {returnedInBulk && (
                <p className="body-sm-regular text-(--font-body-2)">
                  {paidToSupportedAccount > 0 ? t('returnedPayment.bulkAskOneByOne') : t('returnedPayment.noAskNoneInBulk')}
                </p>
              )}

              <div className={styles.emailOption}>
                <ToggleSwitch
                  label={t('officeComments.showInPersonalArea')}
                  checked={showInPersonalArea}
                  onChange={setShowInPersonalArea}
                />
                <ToggleSwitch label={t('sendEmailNotification')} checked={sendEmail} onChange={setSendEmail} />
              </div>

              <div className={styles.commentSection}>
                <label htmlFor="bulk-comment" className={`body-md-semibold ${styles.label}`}>
                  {t('bulkSupportRequestStatus.comment')}
                </label>
                <textarea
                  id="bulk-comment"
                  className={styles.textarea}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('bulkSupportRequestStatus.commentPlaceholder')}
                  rows={3}
                />
                {/* Keeps its line when hidden, so turning the mail on or off does not move the buttons. */}
                <p className="body-xs-sm-regular text-(--font-body-2)" style={{ visibility: mailsCoordinators ? 'visible' : 'hidden' }}>
                  {t('noteSentByEmail')}
                </p>
              </div>

              <div className={styles.actions}>
                <button
                  className={styles.secondaryButton}
                  onClick={handleClose}
                >
                  {t('cancel')}
                </button>
                <button
                  className={styles.primaryButton}
                  onClick={handleSubmit}
                  disabled={!selectedStatus || gateOpen}
                >
                  {mailsCoordinators ? t('saveAndSend') : t('confirm')}
                </button>
              </div>
            </div>
          )}

          {/* State 2: Loading */}
          {dialogState === 'loading' && (
            <div className={styles.content}>
              <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p className="body-md-regular">{t('bulkSupportRequestStatus.updatingStatuses')}</p>
              </div>
            </div>
          )}

          {/* State 3: Success */}
          {dialogState === 'success' && (
            <div className={styles.content}>
              <div className={styles.successContainer}>
                <img src={successIcon} alt="" className={styles.successIcon} />
                <p className={`body-md-semibold ${styles.successTitle}`}>
                  {t('bulkSupportRequestStatus.success')}
                </p>
                <p className={`body-sm-regular ${styles.successDescription}`}>
                  {t('bulkSupportRequestStatus.successDescription', {
                    count: selectedRequests.length,
                  })}
                </p>
              </div>
              <div className={styles.actions}>
                <button
                  className={styles.primaryButton}
                  onClick={handleSuccessClose}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          )}

          {/* State 4: Error */}
          {dialogState === 'error' && (
            <div className={styles.content}>
              <div className={styles.errorSection}>
                <p className={styles.errorMessage}>{t('bulkSupportRequestStatus.failed')}</p>
                {error && <p className={styles.errorDetails}>{error}</p>}
              </div>

              <div className={styles.actions}>
                <button
                  className={styles.secondaryButton}
                  onClick={handleClose}
                >
                  {t('close')}
                </button>
                <button
                  className={styles.primaryButton}
                  onClick={handleRetry}
                >
                  {t('tryAgain')}
                </button>
              </div>
            </div>
          )}
      </MainDialogLayout>
    </>
  );
};

export default BulkStatusChangeDialog;

